/**
 * 予約中タブの図書館ごとの見出し
 *
 * 受取可の本がある館では、受取期限・受取館と「まとめて受け取った」ボタンを出す。
 * 受け取りは館単位で一括に記録する (本ごとには記録しない)。受取 TODO もサーバ側で完了になる。
 */

import { HandHelping, Library, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { confirmDialog } from '@/lib/confirm-dialog'
import { formatPickupDate } from '@/lib/library-pickup'
import { useLibraryFavoritesStore } from '@/store/useLibraryFavoritesStore'

import type { ReservedLibraryGroup } from '../lib/reserved-groups'

type Props = {
  group: ReservedLibraryGroup
}

export const ReservedGroupHeader = ({ group }: Props) => {
  const receivePickups = useLibraryFavoritesStore((s) => s.receivePickups)
  const [pending, setPending] = useState(false)
  const { libraryId, readyCount, pickupDeadline, pickupBranches } = group
  const place = pickupBranches.length > 0 ? pickupBranches.join('・') : group.name

  const handleReceive = async () => {
    if (libraryId == null) return
    const ok = await confirmDialog({
      title: `${place}で受け取りましたか？`,
      description: `受取可の ${readyCount} 冊をまとめて受取済みにします。受取 TODO も完了になります。`,
      confirmLabel: '受け取った',
    })
    if (!ok) return
    setPending(true)
    const success = await receivePickups(libraryId)
    setPending(false)
    if (success) toast.success(`${readyCount} 冊を受取済みにしました`)
    else toast.error('受け取りの記録に失敗しました')
  }

  return (
    // -mx-4 px-4 でスクロール領域の左右パディングぶんまで背景を伸ばす
    <div className="sticky top-0 z-10 -mx-4 flex flex-col gap-1.5 border-b border-border/40 bg-background/95 px-4 py-2 text-xs font-medium backdrop-blur">
      <div className="flex items-center gap-1.5 text-emerald-300">
        <Library className="size-3.5 shrink-0" />
        <span className="min-w-0 truncate">{group.name}</span>
        <span className="shrink-0 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px]">
          {group.books.length}冊
        </span>
      </div>
      {readyCount > 0 && libraryId != null && (
        <div className="flex items-center gap-2 rounded-md border border-amber-400/50 bg-amber-400/10 px-2.5 py-1.5 text-amber-300">
          <HandHelping className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            受取可 {readyCount}冊{pickupDeadline && `・${formatPickupDate(pickupDeadline)}まで`}・{place}
          </span>
          <Button type="button" size="sm" disabled={pending} onClick={() => void handleReceive()} className="shrink-0">
            {pending && <Loader2 className="size-3.5 animate-spin" />}
            まとめて受け取った
          </Button>
        </div>
      )}
    </div>
  )
}
